import { GradientParser } from '../../lib/gradient';
import { extractShaderColors, isShaderGradientFill, renderShaderFill } from './shader-fill';
import { isGradientPatternFill, createGradientPattern } from './gradient-pattern';
import { loadImageCache } from '../image';
import { getNodeImageCache } from '../../provider';

// "url(\"https://www.transparenttextures.com/patterns/old-map.png\")"
// Also supports data URIs like: url("data:image/svg+xml,%3Csvg...")
export function isURLFill(fill) {
  if (typeof fill !== 'string') return false;
  // Match CSS url() pattern with optional quotes and whitespace
  // Use a more permissive regex that handles complex URLs including data URIs with special characters
  const trimmed = fill.trim();
  if (!trimmed.startsWith('url(') || !trimmed.endsWith(')')) return false;
  return true;
}

// "url(\"https://www.transparenttextures.com/patterns/old-map.png\")"
// Also supports data URIs like: url("data:image/svg+xml,%3Csvg...")
// Supports web background-image url() syntax including:
// - url(path/to/image.png)
// - url("path/to/image.png")
// - url('path/to/image.png')
// - url("data:image/svg+xml,...")
// - url("data:image/png;base64,...")
export function parseURLFill(fill) {
  if (!isURLFill(fill)) return null;

  const trimmed = fill.trim();
  // Remove 'url(' prefix and ')' suffix
  let inner = trimmed.slice(4, -1).trim();

  // Handle quoted URLs (single or double quotes)
  if (
    (inner.startsWith('"') && inner.endsWith('"')) ||
    (inner.startsWith("'") && inner.endsWith("'"))
  ) {
    inner = inner.slice(1, -1);
  }

  // Handle escaped characters in URLs
  // CSS allows escaping quotes and other characters with backslash
  inner = inner.replace(/\\(.)/g, '$1');

  return inner || null;
}

/**
 * Get node bounds with absolute dimensions
 */
function getNodeBounds(node) {
  return {
    x: node.x,
    y: node.y,
    w: Math.abs(node.w),
    h: Math.abs(node.h),
  };
}

/**
 * Handle shader gradient fill with fallback support
 * @returns {boolean} true if fill was handled (render complete or should stop)
 */
function handleShaderFill(renderContext, fill, node, render) {
  const { context } = renderContext;
  const handled = renderShaderFill(renderContext, fill, node);
  if (handled) return true;

  // Graceful fallback using provided colors
  const colors = extractShaderColors(fill);
  if (colors.length >= 2) {
    const stops = colors.map((c, i) => `${c} ${(i / (colors.length - 1)) * 100}%`).join(', ');
    const gradient = `linear-gradient(90deg, ${stops})`;
    GradientParser.create(context, gradient, getNodeBounds(node), render);
    return true;
  }

  if (colors.length === 1) {
    context.fillStyle = colors[0];
    render();
    return true;
  }

  // Not handled, allow fallthrough to other fill types
  return false;
}

/**
 * Handle gradient pattern fill (gradient + background-size for tiled patterns)
 * @returns {boolean} true if fill was handled
 */
function handleGradientPatternFill(renderContext, fill, node, render) {
  const { context } = renderContext;
  const isExport = renderContext.mode === 'export';
  const cacheKey = `${fill}_${Math.abs(node.w)}_${Math.abs(node.h)}`;

  let pattern;
  if (!isExport && renderContext.cache?.gradientPattern) {
    pattern = renderContext.cache.gradientPattern.get(cacheKey);
    if (!pattern) {
      pattern = createGradientPattern(context, fill, { w: node.w, h: node.h });
      if (pattern) {
        renderContext.cache.gradientPattern.set(cacheKey, pattern);
      }
    }
  } else {
    pattern = createGradientPattern(context, fill, { w: node.w, h: node.h });
  }

  if (pattern) {
    context.fillStyle = pattern;
    render();
  }
  return true;
}

/**
 * Handle CSS gradient fill
 */
function handleGradientFill(renderContext, fill, node, render) {
  GradientParser.create(renderContext.context, fill, getNodeBounds(node), render);
}

/**
 * Handle URL-based image fill (including data URIs)
 * @returns {boolean} true if fill was handled
 */
function handleURLFill(renderContext, fill, node, render) {
  const { context } = renderContext;
  const url = parseURLFill(fill);

  if (!url) {
    context.fillStyle = '#ffffff';
    render();
    return true;
  }

  loadImageCache(renderContext, node);
  const imgCache = getNodeImageCache(renderContext, node.id);

  // Wait for image to load before creating pattern
  if (!imgCache.img || imgCache.loading) {
    return true; // Still loading, will re-render when ready via markNeedRender
  }

  // Cache pattern to avoid recreating on every render
  if (!imgCache.pattern) {
    imgCache.pattern = context.createPattern(imgCache.img, 'repeat');
  }

  if (imgCache.pattern) {
    context.fillStyle = imgCache.pattern;
    render();
  }
  return true;
}

/**
 * Handle solid color fill
 */
const FILL_NONE = 'none';
const FILL_WHITE = '#ffffff';

function handleSolidFill(renderContext, fill, render) {
  if (fill === FILL_NONE) return;
  renderContext.context.fillStyle = fill || FILL_WHITE;
  render();
}

/**
 * Create and apply fill to the canvas context
 * Supports: shader gradients, gradient patterns, CSS gradients, URL images, solid colors
 */
export function createFill(renderContext, fill, node, render) {
  const { context } = renderContext;

  try {
    const renderFn = render || (() => context.fill());

    // Shader gradient fill
    if (isShaderGradientFill(fill)) {
      if (handleShaderFill(renderContext, fill, node, renderFn)) return;
      // If not handled, fallthrough to other types for graceful degrade
    }

    // Gradient pattern fill (tiled gradients)
    if (isGradientPatternFill(fill)) {
      handleGradientPatternFill(renderContext, fill, node, renderFn);
      return;
    }

    // CSS gradient fill
    if (GradientParser.is(fill)) {
      handleGradientFill(renderContext, fill, node, renderFn);
      return;
    }

    // URL-based image fill
    if (isURLFill(fill)) {
      handleURLFill(renderContext, fill, node, renderFn);
      return;
    }

    // Solid color fill (default)
    handleSolidFill(renderContext, fill, renderFn);
  } catch (e) {
    // Silently handle errors
    if (renderContext.debug) {
      console.error('createFill', e);
    }
  }
}
